import React, { useState } from "react";
import {
    Card,
    Table,
    Tag,
    Button,
    Space,
    Avatar,
    Typography,
    Row,
    Col,
    Pagination,
    Select,
    Input,
    Tooltip,
    Empty,
    Spin,
    Alert,
    Descriptions,
    Modal,
    Tabs,
    Divider
} from "antd";
import {
    EyeOutlined,
    SearchOutlined,
    ReloadOutlined,
    UserOutlined,
    TeamOutlined,
    DollarOutlined,
    ClockCircleOutlined,
    CalendarOutlined,
    PhoneOutlined,
    MailOutlined,
    EnvironmentOutlined
} from "@ant-design/icons";
import useExpertPurchases from "../../Hooks/useExpertPurchase";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Search } = Input;

const ExpertServiceManagement = () => {
    const [searchText, setSearchText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    const [activeTab, setActiveTab] = useState("all");

    const token = localStorage.getItem("token");
    const { data, loading, error, meta, refresh } = useExpertPurchases(token);

    // Format date
    const formatDate = (dateString) => {
        return dayjs(dateString).format("DD MMM YYYY, HH:mm");
    };

    // Format relative time
    const formatRelativeTime = (dateString) => {
        return dayjs(dateString).fromNow();
    };

    // Format currency
    const formatCurrency = (amount, currency = "USD") => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            minimumFractionDigits: 2
        }).format(amount);
    };

    // Format minutes to hours
    const formatMinutesToHours = (minutes) => {
        if (!minutes || minutes <= 0) return "Not specified";
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        if (hours > 0 && remainingMinutes > 0) {
            return `${hours}h ${remainingMinutes}m`;
        }
        if (hours > 0) {
            return `${hours}h`;
        }
        return `${remainingMinutes}m`;
    };



    // Filter data based on search text
    const filteredData = data?.filter(item => {
        if (!searchText) return true;
        const lowerSearchText = searchText.toLowerCase();
        return (
            (item.learner?.firstName || "").toLowerCase().includes(lowerSearchText) ||
            (item.learner?.lastName || "").toLowerCase().includes(lowerSearchText) ||
            (item.learner?.learnerCode || "").toLowerCase().includes(lowerSearchText) ||
            (item.expertService?.title || "").toLowerCase().includes(lowerSearchText) ||
            (item.expertService?.expert?.firstName || "").toLowerCase().includes(lowerSearchText) ||
            (item.expertService?.expert?.lastName || "").toLowerCase().includes(lowerSearchText)
        );
    }) || [];

    // Handle pagination
    const handlePageChange = (page, size) => {
        setCurrentPage(page);
        setPageSize(size);
        refresh(page, size);
    };

    // Handle view details
    const handleViewDetails = (purchase) => {
        setSelectedPurchase(purchase);
        setDetailModalVisible(true);
    };

    // Table columns
    const columns = [
        {
            title: "Learner",
            key: "learner",
            render: (_, record) => (
                <div className="flex items-center">
                    <Avatar
                        src={`${process.env.REACT_APP_URL}${record.learner.profileImage}`}
                        icon={<UserOutlined />}
                        size="small"
                        className="mr-3"
                    />
                    <div>
                        <div className="font-medium">
                            {record.learner.firstName} {record.learner.lastName}
                        </div>
                        <div className="text-xs text-gray-400">
                            {record.learner.learnerCode}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            title: "Service",
            key: "service",
            render: (_, record) => (
                <div>
                    <div className="font-medium">{record.expertService.title}</div>
                    <div className="text-xs text-gray-400">
                        by {record.expertService.expert.firstName} {record.expertService.expert.lastName}
                    </div>
                </div>
            ),
        },
        {
            title: "Amount",
            dataIndex: "amount",
            key: "amount",
            render: (amount, record) => (
                <Text strong>
                    {formatCurrency(amount, record.currency)}
                </Text>
            ),
        },
        {
            title: "Duration",
            key: "duration",
            render: (_, record) => (
                <div>
                    <div>{formatMinutesToHours(record.totalMinutes)} Total</div>
                    <div className="text-xs text-gray-400">
                        {record.consumedMinutes} minutes consumed
                    </div>
                </div>
            ),
        },
        {
            title: "Paid At",
            dataIndex: "paidAt",
            key: "paidAt",
            render: (date) => (
                <div>
                    <div>{formatDate(date)}</div>
                    <div className="text-xs text-gray-400">
                        {formatRelativeTime(date)}
                    </div>
                </div>
            ),
        },

        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => handleViewDetails(record)}
                >
                    View Details
                </Button>
            ),
        },
    ];

    if (loading) {
        return (
            <div className="flex flex-col justify-center items-center p-10">
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>Loading expert service purchases...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-10">
                <Alert
                    message="Error Loading Data"
                    description={error}
                    type="error"
                    showIcon
                    action={
                        <Button size="small" onClick={() => refresh()}>
                            Retry
                        </Button>
                    }
                />
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <Title level={2} style={{ margin: 0 }}>
                    Expert Service Purchases
                </Title>
                <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={() => refresh()}
                >
                    Refresh Data
                </Button>
            </div>

            <Card className="mb-6">
                <Row gutter={16} align="middle">
                    <Col flex="auto">
                        <Search
                            placeholder="Search by learner name, code, service title, or expert name"
                            allowClear
                            enterButton={<SearchOutlined />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ width: '100%' }}
                        />
                    </Col>
                </Row>
            </Card>

            <Card>
                <Table
                    columns={columns}
                    dataSource={filteredData}
                    rowKey="id"
                    scroll={{ x: "max-content" }}
                    pagination={false}
                    loading={loading}
                    locale={{
                        emptyText: (
                            <Empty
                                description="No expert service purchases found"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        )
                    }}
                />

                {meta.totalCount > 0 && (
                    <div className="mt-4 flex justify-end">
                        <Pagination
                            current={currentPage}
                            pageSize={pageSize}
                            total={meta.totalCount}
                            onChange={handlePageChange}
                            showSizeChanger
                            showTotal={(total, range) =>
                                `${range[0]}-${range[1]} of ${total} items`
                            }
                            pageSizeOptions={['10', '20', '50', '100']}
                        />
                    </div>
                )}
            </Card>

            {/* Detail Modal */}
            <Modal
                title="Purchase Details"
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setDetailModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={800}
            >
                {selectedPurchase && (
                    <Tabs
                        defaultActiveKey="1"
                        items={[
                            {
                                key: "1",
                                label: "Purchase Information",
                                children: (
                                    <Descriptions bordered column={2}>
                                        <Descriptions.Item label="Purchase ID">
                                            {selectedPurchase.id}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Enrollment ID">
                                            {selectedPurchase.enrollmentId}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Amount">
                                            {formatCurrency(selectedPurchase.amount, selectedPurchase.currency)}
                                        </Descriptions.Item>

                                        <Descriptions.Item label="Paid At">
                                            {formatDate(selectedPurchase.paidAt)}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Total Minutes">
                                            {formatMinutesToHours(selectedPurchase.totalMinutes)}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Consumed Minutes">
                                            {formatMinutesToHours(selectedPurchase.consumedMinutes)}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Remaining Minutes">
                                            {formatMinutesToHours(selectedPurchase.totalMinutes - selectedPurchase.consumedMinutes)}
                                        </Descriptions.Item>
                                    </Descriptions>
                                ),
                            },
                            {
                                key: "2",
                                label: "Learner Information",
                                children: (
                                    <Descriptions bordered column={2}>
                                        <Descriptions.Item label="Name" span={2}>
                                            <div className="flex items-center">
                                                <Avatar
                                                    src={`${process.env.REACT_APP_URL}${selectedPurchase.learner.profileImage}`}
                                                    icon={<UserOutlined />}
                                                    size="small"
                                                    className="mr-3"
                                                />
                                                {selectedPurchase.learner.firstName} {selectedPurchase.learner.lastName}
                                            </div>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Learner Code">
                                            {selectedPurchase.learner.learnerCode}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="User ID">
                                            {selectedPurchase.learner.userId}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Email">
                                            <Space>
                                                <MailOutlined />
                                                {selectedPurchase.learner.email}
                                            </Space>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Phone">
                                            <Space>
                                                <PhoneOutlined />
                                                {selectedPurchase.learner.phoneNo}
                                            </Space>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Location" span={2}>
                                            <Space>
                                                <EnvironmentOutlined />
                                                {selectedPurchase.learner.city}, {selectedPurchase.learner.state}, {selectedPurchase.learner.country}
                                            </Space>
                                        </Descriptions.Item>
                                    </Descriptions>
                                ),
                            },
                            {
                                key: "3",
                                label: "Expert Service Information",
                                children: (
                                    <Descriptions bordered column={2}>
                                        <Descriptions.Item label="Service Title" span={2}>
                                            {selectedPurchase.expertService.title}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Description" span={2}>
                                            <Paragraph ellipsis={{ rows: 3, expandable: true }}>
                                                {selectedPurchase.expertService.description}
                                            </Paragraph>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Expert" span={2}>
                                            <div className="flex items-center">
                                                <Avatar
                                                    src={`${process.env.REACT_APP_URL}${selectedPurchase.expertService.expert.profileImage}`}
                                                    icon={<UserOutlined />}
                                                    size="small"
                                                    className="mr-3"
                                                />
                                                <div>
                                                    <div>
                                                        {selectedPurchase.expertService.expert.firstName} {selectedPurchase.expertService.expert.lastName}
                                                    </div>
                                                    <div className="text-xs text-gray-400">
                                                        {selectedPurchase.expertService.expert.expertCode} • {selectedPurchase.expertService.expert.yearsOfExperience} years experience
                                                    </div>
                                                </div>
                                            </div>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Unit Price">
                                            {formatCurrency(selectedPurchase.pricing.unitPrice, selectedPurchase.pricing.currency)}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Unit Duration">
                                            {formatMinutesToHours(selectedPurchase.pricing.unitDurationMinutes)}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Notes" span={2}>
                                            {selectedPurchase.pricing.notes || "No notes available"}
                                        </Descriptions.Item>
                                    </Descriptions>
                                ),
                            },
                        ]}
                    />
                )}
            </Modal>
        </div>
    );
};

export default ExpertServiceManagement;